import { useEffect, useState } from 'react';
import { useOptionalWorkspace } from '@/workspace/context';
import { meetingsApi } from '@/workspace/meetings-api';
import { audienceLabel } from '@/workspace/sharing-api';
import type { CompanyMeeting } from '@/workspace/types';
import { useNamespaceCopy } from '@/i18n/ns';
import memberNotesCopy from '@/i18n/ns/meeting-member-notes';
import { useCompany } from '@/company/context';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { actionSurfaceCopy } from './action-surface-copy';

// Display the readable destination; the server binds approval to its audience
// snapshot and rechecks access at execution. This read confers no authority.
export function MeetingNoteDestination({ meetingId }: { meetingId: string }) {
  const ws = useOptionalWorkspace();
  const company = useCompany();
  const copy = useNamespaceCopy(memberNotesCopy);
  const locale = useChatInterfaceLocale();
  const actionCopy = actionSurfaceCopy(locale);
  const [meeting, setMeeting] = useState<CompanyMeeting | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setMeeting(null);
    setFailed(false);
    void meetingsApi
      .get(meetingId)
      .then((meeting) => {
        if (active) setMeeting(meeting);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [meetingId]);
  return (
    <div className="ws-member-note-destination">
      {meeting ? (
        <>
          <button
            className="ws-text-button"
            onClick={() => ws?.openCanvas({ kind: 'meeting', id: meeting.id })}
          >
            {meeting.title}
          </button>
          <p className="ws-muted">
            {audienceLabel({ ...meeting, status: undefined }, company.data?.company?.name, locale)}{' '}
            · {copy.access}
          </p>
        </>
      ) : (
        <p className="ws-muted" role="status">
          {failed ? actionCopy.denied : actionCopy.loading}
        </p>
      )}
    </div>
  );
}
